//internal-deps
import { VALID_PAYMENT_METHODS, isEmpty, PAYLATER_TYPES } from "@swiggy-private/payment-core";
import { format } from "@swiggy-private/js-utils";
import { TEXTS } from "@payment-ui/constants";
import { ITypography } from "@swiggy-private/dls-web-common/dist/components/types";

//defs
import {
    CardPaymentMethodData,
    CardPaymentMethodMeta,
    CodConsentInfo,
    PaymentGroupActions,
    QuickCheckoutTurboVariantData,
    TurboPaymentCommsType,
    TurboPaymentMethod,
    TurboPaymentVariant,
    TurboCommsConfig,
    TurboVariantData,
    WalletPaymentGroupActions,
} from "@payment-ui/interfaces";
import {
    getCardDisplayName,
    getCardPaymentMethodExtraParams,
    getCardPromoMsg,
    isCardPaymentMethod,
    isFoodCard,
} from "./Card";
import { getWalletPaymentMethodExtraParams, isWalletPaymentMethod } from "./Wallet";
import { isUpiCollectMethod, isUpiIntentMethod } from "./Upi";
import { isSwiggyInstrumentPaymentMethod } from "./SwiggyInstrument";
import {
    getPaymentMethodDiscount,
    getPaymentMethodDisplayName,
    getPaymentMethodIconUrl,
    getPaymentMethodPromoMsg,
} from "./Payment";
import { getInAppPaymentMethodExtraParams, isInAppUpiPaymentMethod } from "./InAppUpi";

// constants
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-ui/constants";
import { PAY_USING, TURBO_PAYMENT_COMMS } from "@payment-ui/constants/TurboPayment";
import { TURBO_PAYMENT_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { LOCAL_STORAGE_KEYS, LocalStorage } from "@swiggy-private/core-ui";
import { isBillToCompanyPaymentMethod } from "./BillToCompany";

export const getCardPaymentMethodSubName = ({
    displayName,
    meta,
    isTurboPaymentMethod,
}: {
    displayName: string;
    meta: CardPaymentMethodData["meta"];
    isTurboPaymentMethod: boolean;
}) => {
    if (isTurboPaymentMethod || !isEmpty(meta.nickname) || !isEmpty(meta.cardIssuer)) {
        return displayName.slice(-4) ?? "";
    }

    return "";
};

export const getTurboPaymentPresentationalInfo = (methodData: TurboPaymentMethod, balanceValue?: string | number) => {
    const iconUrl = getPaymentMethodIconUrl(methodData);
    const displayName = getPaymentMethodDisplayName(methodData);
    const discount = getPaymentMethodDiscount(methodData);
    let promoMsg = getPaymentMethodPromoMsg(methodData);

    // For Payment Methods eligible for 'Pay Using...', we need to
    // show them only if promo message is empty to avoid UI being too heavy
    const showPayUsingSuperScript =
        (isUpiCollectMethod(methodData) || isUpiIntentMethod(methodData)) && isEmpty(promoMsg);
    const isCardGroup = isCardPaymentMethod(methodData);
    let paymentMethodSpecificDisplayName, cardSubName;

    if (isCardGroup) {
        const meta = methodData.meta as CardPaymentMethodMeta;
        paymentMethodSpecificDisplayName = getCardDisplayName(methodData.displayName, meta, true);

        cardSubName = getCardPaymentMethodSubName({
            displayName,
            meta,
            isTurboPaymentMethod: true,
        });

        promoMsg = getCardPromoMsg(methodData as CardPaymentMethodData, true);
    }

    const isAmazonPayLater = methodData.paymentCode?.toUpperCase() === PAYLATER_TYPES.AMAZON_PAYLATER?.toUpperCase();
    if (isAmazonPayLater && !Number.isNaN(balanceValue)) {
        promoMsg = format(TEXTS.AVAILABLE_CREDIT_X, Number(balanceValue).toLocaleString("en-IN"));
    }

    const balanceMeta = {
        show: true,
        value: balanceValue,
    };
    const showInlineBalance =
        isWalletPaymentMethod(methodData) ||
        isSwiggyInstrumentPaymentMethod(methodData) ||
        isInAppUpiPaymentMethod(methodData);
    const showNewLineBalance = isFoodCard(methodData as CardPaymentMethodData);

    const payLabel = isBillToCompanyPaymentMethod(methodData) ? methodData.meta.payLabel : PAY_USING;

    return {
        iconUrl,
        showPayUsingSuperScript,
        methodDisplayName: paymentMethodSpecificDisplayName || displayName,
        promoMsg,
        discount,
        balanceMeta,
        showInlineBalance,
        showNewLineBalance,
        cardSubName,
        payLabel,
    };
};

export const isQuickCheckoutVariant = (variant?: TurboPaymentVariant): boolean =>
    variant === TurboPaymentVariant.QUICK_CHECKOUT;

/** Returns `variantData` only when quick checkout is the active variant, so other variants cannot read it. */
export const getQuickCheckoutVariantData = (
    variant?: TurboPaymentVariant,
    variantData?: TurboVariantData,
): QuickCheckoutTurboVariantData | undefined => (isQuickCheckoutVariant(variant) ? variantData : undefined);

export const isOfferCommsFrequencyLimitReached = (offerCommsShownCount: string | null) => {
    return offerCommsShownCount && Number(offerCommsShownCount) >= 3;
};

export const getCommsText = (method?: string, isTurboV2?: boolean): string => {
    if (isTurboV2) {
        return TURBO_PAYMENT_COMMS.TO_PAY_V2;
    }
    if (method?.toUpperCase() === VALID_PAYMENT_METHODS.COD) {
        return TURBO_PAYMENT_COMMS.SLIDE_TO_PAY_FOR_COD;
    }
    return TURBO_PAYMENT_COMMS.SLIDE_TO_PAY;
};

/**
 * Returns the configuration for the Turbo Payment widget communication based on the communication type and offer communication text.
 * @param commsType The type of communication to be displayed on the widget.
 * @param offerCommsText The offer communication text to be displayed on the widget sent from backend.
 * @returns An object containing the text, data test id and text variant for the communication, or null if commsType is null.
 */
export const getTurboPayWidgetCommsConfig = ({
    commsType,
    offerCommsText,
    method,
    isTurboV2,
}: {
    commsType: TurboPaymentCommsType | null;
    offerCommsText: string | null;
    method?: string;
    isTurboV2?: boolean;
}): TurboCommsConfig | null => {
    const commsConfigMap = {
        [TurboPaymentCommsType.EDU_COMMS]: {
            text: getCommsText(method, isTurboV2),
            commsDataTestId: TURBO_PAYMENT_WIDGET_DATA_TEST_IDS.EDU_COMMS,
            textVariant: "V2.body3.neutral.medium" as keyof ITypography,
        },
        [TurboPaymentCommsType.OFFER_COMMS]: {
            // null checking in addition to isEmpty to convince typescript
            text:
                offerCommsText && !isEmpty(offerCommsText) ? offerCommsText : TURBO_PAYMENT_COMMS.OFFER_COMMS_FALLBACK,
            commsDataTestId: TURBO_PAYMENT_WIDGET_DATA_TEST_IDS.OFFER_COMMS,
            textVariant: "V2.body2.neutral.medium" as keyof ITypography,
        },
    };
    return commsType ? commsConfigMap[commsType] : null;
};

export const getPodSubtleVariantWaitTimeInMs = (halfcardInfo: CodConsentInfo): number | undefined => {
    const waitTimeInSecs = Number(halfcardInfo.waitTime);

    if (isNaN(waitTimeInSecs)) {
        return;
    }
    return waitTimeInSecs * 1000;
};

/**
 * Pass `disableEduComms` from a route-aware caller (e.g. Quick Checkout footer) so edu is
 * skipped without writing TURBO_PAYMENT_EDUCATION_COMMS_SHOWN (full cart can still educate later).
 */
export const getCommsToShow = (
    hasOfferComms: boolean,
    options?: { disableEduComms?: boolean },
): TurboPaymentCommsType | null => {
    const eduCommsKey = LOCAL_STORAGE_KEYS.TURBO_PAYMENT_EDUCATION_COMMS_SHOWN;
    const eduCommsKeyTimestamp = LOCAL_STORAGE_KEYS.TURBO_PAYMENT_EDUCATION_COMMS_SHOWN_TIMESTAMP;
    const offerCommsKey = LOCAL_STORAGE_KEYS.TURBO_PAYMENT_OFFER_COMMS_SHOWN_COUNT;
    const offerCommsShownCount = LocalStorage.getItem(offerCommsKey) as string | null;

    let commToShow = null;

    if (hasOfferComms && !isOfferCommsFrequencyLimitReached(offerCommsShownCount)) {
        LocalStorage.setItem(offerCommsKey, Number(LocalStorage.getItem(offerCommsKey) || 0) + 1);
        commToShow = TurboPaymentCommsType.OFFER_COMMS;
    } else if (!options?.disableEduComms && LocalStorage.getItem(eduCommsKey) !== "true") {
        LocalStorage.setItem(eduCommsKey, "true");
        LocalStorage.setItem(eduCommsKeyTimestamp, Date.now());
        commToShow = TurboPaymentCommsType.EDU_COMMS;
    }

    return commToShow;
};

/**
 * Builds the `onPay` extra params for a turbo payment method. Shared by the BAU and quick-checkout
 * method widgets so the per-group param assembly lives in one place.
 * SWIGGY_PAY_AMOUNT is forced undefined because swiggy money is not offered on turbo today.
 */
export const getTurboPayExtraParams = (
    methodData: TurboPaymentMethod,
    paymentActions: PaymentGroupActions<TurboPaymentMethod>,
    cvv?: string,
) => {
    let paymentMethodExtraParams;

    if (isWalletPaymentMethod(methodData)) {
        paymentMethodExtraParams = getWalletPaymentMethodExtraParams(
            methodData,
            paymentActions as WalletPaymentGroupActions,
        );
    }

    if (isCardPaymentMethod(methodData)) {
        paymentMethodExtraParams = getCardPaymentMethodExtraParams(methodData, cvv);
    }

    if (isInAppUpiPaymentMethod(methodData)) {
        paymentMethodExtraParams = getInAppPaymentMethodExtraParams(methodData);
    }

    return {
        [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: undefined,
        ...paymentMethodExtraParams,
    };
};

export const showFlipkartCoinsTooltip = (
    commsType: TurboPaymentCommsType | null,
    hideEducationComms: boolean,
    isFlipkartCoinsEligible: boolean,
) => !commsType && !hideEducationComms && isFlipkartCoinsEligible;
